import { useCallback, useEffect, useRef, useState } from 'react';

/** Events from POST /api/threads-agent/sessions/:id/turns (see threads-agent.service.ts). */
export interface AgentTurnHandlers {
  onMeta?: (data: { turnId: string; userMessageId: number }) => void;
  onStatus?: (data: { label: string; tool: string; toolUseId: string }) => void;
  onTool?: (data: { tool: string; label: string; status: 'ok' | 'failed'; durationMs: number }) => void;
  onToken?: (delta: string) => void;
  /** The answer so far, replacing what streamed (a draft the content filter withheld). */
  onReplace?: (content: string) => void;
  onDone?: (data: AgentTurnDone) => void;
  onError?: (data: AgentTurnError) => void;
}

export interface AgentTurnDone {
  messageId: number | null;
  content: string;
  status: 'complete' | 'stopped' | 'failed';
  /** Only the cited sources; `index` is the [n] used in the answer. */
  sources: Array<{ index?: number; title?: string; url?: string | null }>;
  images?: Array<Record<string, unknown>>;
  /** Changes proposed for approval (see ActionCards); approved on the saved answer. */
  actions?: Array<Record<string, unknown>>;
  /** Trips and clients the answer is about (see AnswerCards). */
  cards?: Array<Record<string, unknown>>;
  followUps?: string[];
  degraded: boolean;
}

/** A file attached to a message: its extracted text (POST /api/threads-agent/attachments). */
export interface AgentAttachment {
  name: string;
  text: string;
}

export interface AgentTurnError {
  code: string;
  message: string;
  retryable: boolean;
}

/** Request body: `send` a message, `regenerate` the last answer, or `edit` an earlier message. */
export interface AgentTurnBody {
  content: string;
  action?: 'send' | 'regenerate' | 'edit';
  targetMessageId?: number;
  /** false when the advisor chose Global in a project thread. */
  projectContext?: boolean;
  attachments?: AgentAttachment[];
}

/** What Stop tells the server: the turn and how much of the answer was on screen. */
export interface AgentTurnStop {
  sessionId: number;
  turnId: string;
  shownChars: number;
}

const STOP_TIMEOUT_MS = 2000;

/** Route one parsed SSE frame to its handler. Returns true for the terminal `done` frame. */
export function dispatchSseFrame(frame: { event: string; data: string }, handlers: AgentTurnHandlers): boolean {
  let data: any;
  try {
    data = JSON.parse(frame.data);
  } catch {
    return false;
  }
  if (frame.event === 'meta') handlers.onMeta?.(data);
  else if (frame.event === 'status') handlers.onStatus?.(data);
  else if (frame.event === 'tool') handlers.onTool?.(data);
  else if (frame.event === 'token') handlers.onToken?.(String(data?.delta ?? ''));
  else if (frame.event === 'replace') handlers.onReplace?.(String(data?.content ?? ''));
  else if (frame.event === 'error') handlers.onError?.(data);
  else if (frame.event === 'done') {
    handlers.onDone?.(data);
    return true;
  }
  return false;
}

/** Split an SSE byte stream into `{event, data}` frames; ignores comments (heartbeats). */
export function parseSseFrames(buffer: string): { frames: Array<{ event: string; data: string }>; rest: string } {
  const frames: Array<{ event: string; data: string }> = [];
  const parts = buffer.split(/\r?\n\r?\n/);
  const rest = parts.pop() ?? '';
  for (const part of parts) {
    let event = 'message';
    const data: string[] = [];
    for (const line of part.split(/\r?\n/)) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) data.push(line.slice(5).trimStart());
    }
    if (data.length) frames.push({ event, data: data.join('\n') });
  }
  return { frames, rest };
}

/**
 * Streams one Threads turn from AgentCore over POST + ReadableStream (the message travels in the
 * body, never the URL). `stop()` freezes the answer on screen, tells the server how much of it was
 * shown (so only that is saved), then aborts the request, which stops the agent server-side.
 */
export function useAgentTurnStream() {
  const controllerRef = useRef<AbortController | null>(null);
  const stoppedRef = useRef<AbortController | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);

  useEffect(() => () => controllerRef.current?.abort(), []);

  const stop = useCallback(async (info?: AgentTurnStop) => {
    const controller = controllerRef.current;
    if (!controller) return;
    stoppedRef.current = controller;
    if (info) {
      const timeout = new AbortController();
      const timer = setTimeout(() => timeout.abort(), STOP_TIMEOUT_MS);
      await fetch(`/api/threads-agent/sessions/${info.sessionId}/turns/${encodeURIComponent(info.turnId)}/stop`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shownChars: info.shownChars }),
        signal: timeout.signal,
      })
        .catch(() => undefined)
        .finally(() => clearTimeout(timer));
    }
    controller.abort();
  }, []);

  const start = useCallback(async (sessionId: number, body: string | AgentTurnBody, handlers: AgentTurnHandlers) => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setIsStreaming(true);
    let finished = false;
    try {
      const response = await fetch(`/api/threads-agent/sessions/${sessionId}/turns`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify(typeof body === 'string' ? { content: body } : body),
        signal: controller.signal,
      });
      if (!response.ok || !response.body) {
        handlers.onError?.({
          code: `HTTP_${response.status}`,
          message: response.status === 404 ? 'This thread could not be found.' : 'The assistant is unavailable right now. Please try again.',
          retryable: response.status >= 500,
        });
        return;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const { frames, rest } = parseSseFrames(buffer);
        buffer = rest;
        // After Stop, nothing more is shown: the server saves only what was on screen.
        if (stoppedRef.current === controller) continue;
        for (const frame of frames) {
          if (dispatchSseFrame(frame, handlers)) finished = true;
        }
      }
      if (!finished && stoppedRef.current === controller) {
        handlers.onDone?.({ messageId: null, content: '', status: 'stopped', sources: [], degraded: false });
      } else if (!finished) {
        handlers.onError?.({ code: 'STREAM_ENDED', message: 'The response was interrupted. Please try again.', retryable: true });
      }
    } catch (err) {
      if (controller.signal.aborted) {
        handlers.onDone?.({ messageId: null, content: '', status: 'stopped', sources: [], degraded: false });
      } else {
        handlers.onError?.({ code: 'NETWORK', message: 'Connection lost. Please try again.', retryable: true });
      }
    } finally {
      if (controllerRef.current === controller) controllerRef.current = null;
      setIsStreaming(false);
    }
  }, []);

  return { start, stop, isStreaming };
}
